import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import {
  GridCsvExportMenuItem,
  GridExcelExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-premium";
import { postSaveAuditLog } from "../api/audit";
import { useCookieData } from "../hook/useCookieData";
import { EXPORT_ROW_LIMIT } from "../constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useCallback, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

// match the type with index, you the list is updated the order should be updated
const EXPORT_MENU_ITEM_TYPES = ['CSV', 'Excel', 'PDF'] as const;

// global variable to track the last processed export event for debouncing
let lastProcessedEvent: { target: HTMLElement | null; timestamp: number; exportType: string } | null = null;
const DEBOUNCE_TIME = 300;

function CustomExportButton(props: any) {
  dayjs.extend(utc);
  const { t } = useTranslation();
  const { userName } = useCookieData();
  const entityType = props.entityType || 'Unknown';
  const moduleName = props.moduleName || 'UAM';
  const entityName = props.entityName || '';
  const containerRef = useRef<HTMLDivElement>(null);
  const totalRowCount = props.totalRowCount;
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);

  const handleExportAuditLog = useCallback((exportType: string) => {
    const operationDate = dayjs().utc().format('DD-MMM-YYYY HH:mm:ss');
    
    const auditLogParams = {
      operatorUsername: userName || '',
      operationType: `Export (${entityType} ${exportType})`,
      entityIdentifier: "",
      entityName: entityName,
      fromService: moduleName,
      operationDate: operationDate,
      entityCreatedByName: userName || '',
      entityCreatedDate: operationDate,
      entityModifiedByName: '',
      entityModifiedDate: '',
      fieldModifications: [{
        fieldName: "",
        oldValue: "",
        newValue: ""
      }],
      reason: '',
    };

    postSaveAuditLog(auditLogParams)?.catch((err: any) => {
      console.error('Failed to save audit log:', err);
    });
  }, [entityType, userName, entityName, moduleName]);

  const isExportBlocked = useCallback(() => {
    return totalRowCount != null && totalRowCount > EXPORT_ROW_LIMIT;
  }, [totalRowCount]);

  useEffect(() => {
    const handleExportClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const menuItem = target.closest('[role="menuitem"]') || target.closest('li[class*="MuiMenuItem"]');

      if (!menuItem) return;

      const menuContainer = menuItem.closest('[role="menu"]') || menuItem.closest('.MuiMenu-paper');
      if (!menuContainer) return;

      const menuId = menuContainer.getAttribute('id');
      if (!menuId || !containerRef.current?.querySelector(`[aria-controls="${menuId}"]`)) {
        return;
      }

      const menuItems = menuContainer.querySelectorAll('[role="menuitem"]');
      const itemIndex = Array.from(menuItems).indexOf(menuItem);
      const exportType = EXPORT_MENU_ITEM_TYPES[itemIndex];
      if (!exportType) return;

      if (isExportBlocked()) {
        e.stopPropagation();
        e.preventDefault();
        setLimitDialogOpen(true);
        return;
      }

      const now = Date.now();
      if (
        lastProcessedEvent &&
        lastProcessedEvent.target === menuItem &&
        lastProcessedEvent.exportType === exportType &&
        now - lastProcessedEvent.timestamp < DEBOUNCE_TIME
      ) {
        return;
      }
      
      lastProcessedEvent = {
        target: menuItem as HTMLElement,
        timestamp: now,
        exportType: exportType
      };
      
      handleExportAuditLog(exportType);
    };

    document.addEventListener('click', handleExportClick, true);
    return () => {
      document.removeEventListener('click', handleExportClick, true);
    };
  }, [handleExportAuditLog, isExportBlocked]);

  return (
    <div ref={containerRef} data-custom-toolbar-audit>
      <GridToolbarExportContainer {...props}>
        <GridCsvExportMenuItem
          options={{
            utf8WithBom: true,
            includeHeaders: true,
          }}
        />
        <GridExcelExportMenuItem/>
        <GridPrintExportMenuItem />
      </GridToolbarExportContainer>
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)}>
        <DialogTitle>{t('audit.exportLimitExceeded.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('audit.exportLimitExceeded.message', { limit: EXPORT_ROW_LIMIT.toLocaleString() })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)} variant="contained">
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function CustomToolbarAudit(props: any) {
  return (
    <GridToolbarContainer
      sx={{ display: "flex", justifyContent: "space-between" }}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <CustomExportButton 
          entityType={props.entityType}
          moduleName={props.moduleName}
          entityName={props.entityName}
          totalRowCount={props.totalRowCount}
        />
        <GridToolbarDensitySelector />
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
      </Stack>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

export default CustomToolbarAudit;
